import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../provider/AuthProvider";
import { registerUser, validateRegistrationCustomer } from "../services/authService";
import { validateEmail } from "../utils/emailUtil";

const passwordIsValid = (password) =>
  password.length >= 12 &&
  /[a-z]/.test(password) &&
  /[A-Z]/.test(password) &&
  /\d/.test(password) &&
  /[^A-Za-z0-9]/.test(password);

export default function RegisterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const registrationVerified = Boolean(
    location.state?.registrationVerified &&
    location.state?.cisloOdberatele &&
    location.state?.email
  );
  const [cisloOdberatele, setCisloOdberatele] = useState(location.state?.cisloOdberatele || "");
  const [registrationEmail, setRegistrationEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaEmail, setMfaEmail] = useState(location.state?.email || "");
  const [telefon, setTelefon] = useState("");
  const [mfaTyp, setMfaTyp] = useState("EMAIL");
  const [inputError, setInputError] = useState("");

  const requestRegistrationCode = async (event) => {
    event.preventDefault();
    if (!cisloOdberatele.trim() || !validateEmail(registrationEmail)) {
      setInputError("Vyplňte číslo odběratele a platný e-mail.");
      return;
    }

    const result = await validateRegistrationCustomer(
      cisloOdberatele.trim(),
      registrationEmail.trim()
    );
    if (!result.success) {
      setInputError(result.message || "Ověření odběratele se nezdařilo.");
      return;
    }

    navigate("/verify-code", {
      state: {
        mode: "register",
        email: registrationEmail.trim(),
        cisloOdberatele: cisloOdberatele.trim(),
      },
    });
  };

  const completeRegistration = async (event) => {
    event.preventDefault();
    if (!passwordIsValid(password)) {
      setInputError(
        "Heslo musí mít alespoň 12 znaků, malé i velké písmeno, číslici a speciální znak."
      );
      return;
    }
    if (password !== confirmPassword) {
      setInputError("Hesla se neshodují.");
      return;
    }
    if (mfaTyp === "EMAIL" && !validateEmail(mfaEmail)) {
      setInputError("Pro e-mailové MFA vyplňte platný e-mail.");
      return;
    }
    if (mfaTyp === "SMS" && !telefon.trim()) {
      setInputError("Pro MFA přes SMS vyplňte telefon.");
      return;
    }

    const result = await registerUser({
      cisloOdberatele: cisloOdberatele.trim(),
      password,
      confirmPassword,
      email: mfaTyp === "EMAIL" ? mfaEmail.trim() : null,
      telefon: mfaTyp === "SMS" ? telefon.trim() : null,
      mfaTyp,
    });
    if (!result.success) {
      setInputError(result.message || "Registrace se nezdařila.");
      return;
    }

    setAccessToken(result.token);
    navigate("/zvk", { state: { showWelcomeModal: true } });
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 420, mx: "auto", my: 2 }}>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Registrace
      </Typography>
      {inputError && <Alert severity="error" sx={{ mb: 2 }}>{inputError}</Alert>}

      {!registrationVerified ? (
        <Box component="form" onSubmit={requestRegistrationCode}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Krok 1 ze 2: ověření odběratele
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Zadejte číslo odběratele a e-mail uvedený ve smlouvě. Po ověření údajů vám na zadaný e-mail pošleme kód.
          </Alert>
          <TextField label="Číslo odběratele" value={cisloOdberatele}
            onChange={(event) => setCisloOdberatele(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          <TextField label="E-mail" type="email" value={registrationEmail}
            onChange={(event) => setRegistrationEmail(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          <Button type="submit" variant="contained" fullWidth>Odeslat ověřovací kód</Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={completeRegistration}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Krok 2 ze 2: nastavte heslo a MFA
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            E-mail {registrationEmail} byl ověřen. Nejprve zadejte heslo, heslo musí mít alespoň 12 znaků, malé i velké písmeno, číslici a speciální znak. Poté zvolte způsob MFA (vícefaktorového ověření) a nakonec zadejte kontaktní údaje.
          </Alert>
          <TextField label="Heslo" type="password" value={password}
            onChange={(event) => setPassword(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          <TextField label="Heslo znovu" type="password" value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          <TextField select label="Typ vícefaktorového ověření (MFA)" value={mfaTyp}
            onChange={(event) => setMfaTyp(event.target.value)} fullWidth required sx={{ mb: 2 }}>
            <MenuItem value="EMAIL">E-mail</MenuItem>
            <MenuItem value="SMS">SMS</MenuItem>
          </TextField>
          {mfaTyp === "EMAIL" ? (
            <TextField label="E-mail pro MFA" type="email" value={mfaEmail}
              onChange={(event) => setMfaEmail(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          ) : (
            <TextField label="Telefon pro MFA" value={telefon}
              onChange={(event) => setTelefon(event.target.value)} fullWidth required sx={{ mb: 2 }} />
          )}
          <Button type="submit" variant="contained" fullWidth>Dokončit registraci</Button>
        </Box>
      )}

      <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => navigate("/login")}>
        Zpět k přihlášení
      </Button>
    </Box>
  );
}
